import { Injectable, Predicate } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { Booking } from '../models/booking';
import { BookingFilter } from '../models/booking.filter';
import {
  AcceptBookingReschedule,
  ActivateDigitalCourseBookingInit,
  ActiveTotalCount,
  AddBookingFilter,
  AddBookingReview,
  AddError,
  AddParticipants,
  AddTotalCount,
  ApproveBooking,
  ApproveBookingSuccess,
  ApproveServiceBooking,
  ApproveServiceBookingSuccess,
  CancelBooking,
  CancelServiceBooking,
  CheckBooking,
  EditBooking,
  FetchBookings,
  FetchMoreBookings,
  FetchSingleBookings,
  GetBooking,
  HistoryTotalCount,
  PaidBooking,
  PendingTotalCount,
  RefundBooking,
  RegisterBooking,
  RegisterMultiBooking,
  RegisterServiceBooking,
  RejectBooking,
  RejectServiceBooking,
  RemoveBookingFilter,
  RemoveIsCourseBooked,
  RemoveParticipants,
  ResetPageIndex,
  SetServiceProviderId,
  SetServiceProviderView,
  SetUserMode,
  UpcomingTotalCount,
  UpdateBooking,
  UpdateBookingSuccess,
  UpdatePageIndex,
  UpdateServiceBookingSuccess,
  SubmitClassroomFeedback,
  SubmitInstructorReview,
  SubmitDigitalCourseReview,
  SubmitClassroomCourseReview,
  ResetBookingStore
} from './booking.action';
import * as selector from './booking.selector';
import { BookingState } from '@modules/booking/store/booking.state';
import { Review } from '@shared/models/review';
import { Participant } from '@modules/course/classroom/models/participant';
import { ReviewReq as InstructorReview } from '@modules/instructor/models/review';
import { Feedback } from '@modules/course/classroom/models/feedback';

@Injectable()
export class BookingStoreService {
  constructor(private store: Store<BookingState>) {}

  public fetchAllBooking(
    search: string = '',
    filter: BookingFilter = new BookingFilter(),
    pageIndex: number = 0,
    pageSize: number = 4,
    sort: string = ''
  ): void {
    this.store.dispatch(
      FetchBookings({
        search: search,
        filter: filter,
        pageIndex: pageIndex,
        pageSize: pageSize,
        sort: sort,
      })
    );
  }

  public fetchMoreBooking(
    search: string = '',
    filter: BookingFilter = new BookingFilter(),
    pageIndex: number = 0,
    pageSize: number = 4,
    sort: string = ''
  ): void {
    this.store.dispatch(
      FetchMoreBookings({
        search: search,
        filter: filter,
        pageIndex: pageIndex,
        pageSize: pageSize,
        sort: sort,
      })
    );
  }

  public fetchAllSingleBooking(
    search: string = '',
    filter: BookingFilter = new BookingFilter(),
    pageIndex: number = 0,
    pageSize: number = 4,
    sort: string = ''
  ): void {
    this.store.dispatch(
      FetchSingleBookings({
        search: search,
        filter: filter,
        pageIndex: pageIndex,
        pageSize: pageSize,
        sort: sort,
      })
    );
  }

  public getAllBookings(): Observable<Booking[]> {
    return this.store.pipe(select(selector.getAllBookings));
  }

  public getBookingsBySearch(predicate: Predicate<Booking>): Observable<Booking[]> {
    return this.store.pipe(select(selector.getBookingBySearch(predicate)));
  }

  public getBookingById(id: string): Observable<Booking> {
    return this.store.select(selector.getBookingById(id));
  }

  public addTotalCount(data: number): void {
    this.store.dispatch(AddTotalCount({ totalCount: data }));
  }

  public getTotalCount(): Observable<number> {
    return this.store.select(selector.getTotalCount);
  }

  public getPageIndex(): Observable<number> {
    return this.store.select(selector.getPageIndex);
  }

  public updatePageIndex(): void {
    this.store.dispatch(UpdatePageIndex());
  }

  public resetPageIndex(): void {
    this.store.dispatch(ResetPageIndex());
  }

  public setActiveTotalCount(data: number): void {
    this.store.dispatch(ActiveTotalCount({ activeCount: data }));
  }

  public getActiveCount(): Observable<number> {
    return this.store.select(selector.getActiveCount);
  }

  public setPendingTotalCount(data: number): void {
    this.store.dispatch(PendingTotalCount({ pendingCount: data }));
  }

  public getPendingCount(): Observable<number> {
    return this.store.select(selector.getPendingCount);
  }

  public setUpcomingTotalCount(data: number): void {
    this.store.dispatch(UpcomingTotalCount({ upcomingCount: data }));
  }

  public getUpcomingCount(): Observable<number> {
    return this.store.select(selector.getUpcomingCount);
  }

  public setHistoryTotalCount(data: number): void {
    this.store.dispatch(HistoryTotalCount({ historyCount: data }));
  }

  public getHistoryCount(): Observable<number> {
    return this.store.select(selector.getHistoryCount);
  }

  public loadError(error: any): void {
    this.store.dispatch(AddError(error));
  }

  public fetchBookingById(id: string): void {
    this.store.dispatch(GetBooking({ id: id }));
  }

  public approveBooking(id: string, booking: Partial<Booking>): void {
    this.store.dispatch(ApproveBooking({ id: id, change: booking }));
  }

  public cancelBooking(id: string, booking: Partial<Booking>): void {
    this.store.dispatch(CancelBooking({ id: id, change: booking }));
  }

  public rejectBooking(id: string, booking: Partial<Booking>): void {
    this.store.dispatch(RejectBooking({ id: id, change: booking }));
  }

  public paidBooking(id: string, booking: Partial<Booking>): void {
    this.store.dispatch(PaidBooking({ id: id, change: booking }));
  }

  public updateBooking(id: string, booking: Partial<Booking>): void {
    this.store.dispatch(EditBooking({ id: id, change: booking }));
  }

  public registerBooking(booking: Partial<Booking>): void {
    this.store.dispatch(RegisterBooking({ change: booking }));
  }

  public activateDigitalCourseBooking(userId:string, bookingId: string): void {
    this.store.dispatch(ActivateDigitalCourseBookingInit({userId,bookingId}));
  }
  public registerMultiBooking(bookings: Partial<Booking[]>, participants: number): void {
    this.store.dispatch(RegisterMultiBooking({ bookings: bookings, participants }));
  }

  public addBookingFilter(filter: BookingFilter): void {
    this.store.dispatch(AddBookingFilter({ filter: filter }));
  }

  public getBookingFilter(): Observable<BookingFilter> {
    return this.store.select(selector.getFilter);
  }

  public removeBookingFilter(): void {
    this.store.dispatch(RemoveBookingFilter());
  }

  public updateBookingSuccess(): void {
    this.store.dispatch(UpdateBookingSuccess());
  }

  public approveBookingSuccess(): void {
    this.store.dispatch(ApproveBookingSuccess());
  }

  public modifyBooking(id: string, booking: Booking): void {
    this.store.dispatch(UpdateBooking({ id: id, change: booking }));
  }

  public setUserMode(isGuide: boolean): void {
    this.store.dispatch(SetUserMode({ isGuide: isGuide }));
  }

  public setServiceProviderView(serviceProviderView: boolean): void {
    this.store.dispatch(SetServiceProviderView( {serviceProviderView}));
  }

  public setServiceProviderId(serviceProviderId: string): void {
    this.store.dispatch(SetServiceProviderId( {serviceProviderId}));
  }

  public getServiceProviderView(): Observable<boolean> {
    return this.store.select(selector.getServiceProviderView);
  }

  public getServiceProviderId(): Observable<string> {
    return this.store.select(selector.getServiceProviderId);
  }

  public getBookingServiceProviderInfo(): Observable<{serviceProviderView:boolean; serviceProviderId: string}> {
    return  this.store.select(selector.getBookingServiceProviderInfo);
  }

  public getUserMode(): Observable<boolean> {
    return this.store.select(selector.getUserMode);
  }

  public addBookingReview(review: Review) {
    this.store.dispatch(AddBookingReview({ review }));
  }

  public getIsCourseBooked(): Observable<boolean> {
    return this.store.select(selector.getIsCourseBooked);
  }

  public checkIsCourseBooked(userId: string, courseId: string) {
    this.store.dispatch(CheckBooking({ userId, courseId }));
  }

  public removeIsCourseBooked() {
    this.store.dispatch(RemoveIsCourseBooked());
  }

  public acceptReschedule(id: string): void {
    this.store.dispatch(AcceptBookingReschedule({ id }));
  }

  public refundBooking(userId: string, bookingId: string): void {
    this.store.dispatch(RefundBooking({ userId, bookingId }));
  }

  public addParticipants(participants: Participant[]): void {
    this.store.dispatch(AddParticipants({ participants }));
  }

  public removeParticipants(): void {
    this.store.dispatch(RemoveParticipants());
  }

  public getParticipants(): Observable<Participant[]> {
    return this.store.select(selector.getParticipants);
  }

  public submitReview(booking: Booking, courseReview: Review, feedback: Feedback | null = null, instructorReview: InstructorReview | null = null): void {
    if (booking.serviceRef.bookingType == 'Course') {
      this.store.dispatch(SubmitDigitalCourseReview({ serviceId: booking.serviceRef.id, review: courseReview }));
    }
    else if (booking.serviceRef.bookingType == 'Classroom') {
      this.store.dispatch(SubmitClassroomCourseReview({ serviceId: booking.serviceRef.id, review: courseReview }));

      if (feedback) {
        this.store.dispatch(SubmitClassroomFeedback({ serviceId: booking.serviceRef.id, feedback }));
      }

      if (instructorReview) {
        this.store.dispatch(SubmitInstructorReview({ instructorId: booking.guide.id, instructorReview }));
      }
    }
  }

  public resetBookingStore(): void {
    this.store.dispatch(ResetBookingStore());
  }

  /**
   * Service related methods
   */
  public registerServiceBooking(booking: Partial<Booking>, isInstantBooking: boolean = false): void {
    this.store.dispatch(RegisterServiceBooking({ change: booking, isInstantBooking }));
  }

  public approveServiceBooking(id: string, booking: Partial<Booking>): void {
    this.store.dispatch(ApproveServiceBooking({ id: id, change: booking }));
  }

  public updateServiceBookingSuccess(): void {
    this.store.dispatch(UpdateServiceBookingSuccess());
  }

  public cancelServiceBooking(id: string, booking: Partial<Booking>): void {
    this.store.dispatch(CancelServiceBooking({ id: id, change: booking }));
  }

  public approveServiceBookingSuccess(): void {
    this.store.dispatch(ApproveServiceBookingSuccess());
  }

  public rejectServiceBooking(id: string, booking: Partial<Booking>): void {
    this.store.dispatch(RejectServiceBooking({ id: id, change: booking }));
  }
}
